import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { FirebaseCachedVideo } from "@components/FirebaseCachedImage/FirebaseCachedVideo";
import { FC, useCallback, useEffect, useRef, useState } from "react";

type Props = {
  items: MediaItem[];
  autoScrollInterval?: number;
};

const AUTO_SCROLL_MS = 4000;
const RESUME_DELAY_MS = 6000;

export const MediaCarousel: FC<Props> = ({ items, autoScrollInterval = AUTO_SCROLL_MS }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const pausedUntilRef = useRef(0);

  const scrollToIndex = useCallback((index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const child = container.children[index] as HTMLElement | undefined;
    if (!child) return;
    container.scrollTo({ left: child.offsetLeft - container.offsetLeft, behavior: "smooth" });
  }, []);

  // Track active index from scroll position
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const center = container.scrollLeft + container.offsetWidth / 2;
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < container.children.length; i++) {
      const child = container.children[i] as HTMLElement;
      const childCenter = child.offsetLeft - container.offsetLeft + child.offsetWidth / 2;
      const dist = Math.abs(center - childCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    setActiveIndex(closest);
  }, []);

  // Pause auto-scroll on user interaction
  const handleInteraction = useCallback(() => {
    pausedUntilRef.current = Date.now() + RESUME_DELAY_MS;
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (items.length <= 1) return;

    const interval = setInterval(() => {
      if (Date.now() < pausedUntilRef.current) return;
      setActiveIndex((prev) => {
        const next = (prev + 1) % items.length;
        scrollToIndex(next);
        return next;
      });
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [items.length, autoScrollInterval, scrollToIndex]);

  if (items.length === 0) return null;

  // Single item - render without carousel chrome
  if (items.length === 1) {
    const item = items[0];
    return item.type === "video" ? (
      <div className="mx-auto max-w-lg p-2">
        <FirebaseCachedVideo uri={item.uri} />
      </div>
    ) : (
      <FirebaseCachedImage
        retries={8}
        showPlaceholder
        uri={`${item.uri}_light`}
        defaultUri={item.defaultUri ?? item.uri}
        alt=""
      />
    );
  }

  return (
    <div className="relative w-full">
      <style>{`
        .media-carousel::-webkit-scrollbar{display:none}
        .media-carousel{-ms-overflow-style:none;scrollbar-width:none}
      `}</style>

      <div
        ref={scrollRef}
        className="media-carousel flex h-[50vh] snap-x snap-mandatory gap-3 overflow-x-auto"
        onScroll={handleScroll}
        onTouchStart={handleInteraction}
        onMouseDown={handleInteraction}
      >
        {items.map((item) => (
          <div
            key={item.id}
            className="grid h-full w-[75%] flex-shrink-0 snap-center place-items-center overflow-hidden [&>*]:col-start-1 [&>*]:row-start-1 [&_img]:max-h-[50vh] [&_img]:object-contain [&_video]:max-h-[50vh] [&_video]:object-contain"
          >
            {item.type === "video" ? (
              <FirebaseCachedVideo uri={item.uri} />
            ) : (
              <FirebaseCachedImage
                retries={8}
                showPlaceholder
                uri={`${item.uri}_light`}
                defaultUri={item.defaultUri ?? item.uri}
                alt=""
              />
            )}
          </div>
        ))}
      </div>

      {/* Dot indicators */}
      <div className="mt-3 flex justify-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            onClick={() => {
              handleInteraction();
              scrollToIndex(i);
            }}
            className={`h-2 w-2 rounded-full transition-colors ${i === activeIndex ? "bg-primary" : "bg-base-300"}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
